import { digger } from '@cbre/digger';
import {
  concatValuesBy,
  defaultTxt,
  formatNumber,
  formatValueWithUom,
  getPercentageWithAmount,
  getPropertyTypeLabel,
  makeAmountObject,
  makeAmountWith,
  UserSettings,
} from '@miq/utils';
import { map } from 'rxjs/operators';
import { ReportRecord } from '../../../../../models/report.model';
import {
  DisplayFnArgumentsInfo,
  DisplayFunctionOptions,
  LovValues,
  TemplateFieldsDisplayFn,
} from '../../../../../models/template.model';
import { sortAttributesByFieldId } from '../../../utils/argument-sorting';
import {
  displayFnWrapper,
  formatYesOrNoValues,
  isDash,
  mapAndFormatDynamicValue,
} from '../../../utils/dynamic-data-retrievers';
import {
  currencyLov,
  getCompanyAndContactInfoByRoles,
  PropertyType,
  propertyTypeDisplayFnFactory,
  valueWithUomPrefix,
} from '../us.common';
export const getAvailVacant = (record, options) => {
  /*
   * NOTE: due to groupReducer (condensed view helper) also groups records with
   * no associated property, validating that a method exists is required.
   */
  const usage = getAvailabilityDetailsByUsageAndTemplate(record, options);
  const nra = usage.getAsAmount('net_rentable_area', 'net_rentable_area_uom_desc');
  const dataset = [
    { value: 'total_available_surface_area', uom: 'total_available_surface_area_uom_desc' },
    { value: 'total_vacant_area', uom: 'total_vacant_area_uom_desc' },
  ];
  const availableArea = makeAmountObject(usage, dataset[0]);
  const vacantArea = makeAmountObject(usage, dataset[1]);
  if (!usage || usage.getAsAmount == null) {
    return defaultTxt;
  }

  if (nra != null && !!nra.value) {
    const availablePerc = getPercentageWithAmount(makeAmountWith(availableArea), nra);
    const vacantPerc = getPercentageWithAmount(makeAmountWith(vacantArea), nra);
    return concatValuesBy(
      undefined,
      formatValueWithUom({
        value: availablePerc,
        uom: '%',
        defaultTxt,
        digits: '.1-1',
      }),
      formatValueWithUom({
        value: vacantPerc,
        uom: '%',
        defaultTxt,
        digits: '.1-1',
      })
    );
  }
  return defaultTxt;
};

const getUsageFieldByTemplate = (record, valueKey: string, uomKey: string) => {
  if (record[valueKey] == null) {
    return defaultTxt;
  }
  return formatValueWithUom({
    value: record[valueKey],
    uom: record[uomKey],
    defaultTxt,
  });
};

//usage object based on the selected template
export const getAvailabilityDetailsByUsageAndTemplate = (record, options) => {
  const selectedTemplate = options?.templateFieldsCatalog.find(t => t.templateId == options.templateId);
  if (selectedTemplate?.templateName) {
    const usages = record.associatedProperty.usages || [];
    const matchedUsage = usages.find(usage => {
      const usageDesc = String(usage.ref_property_usage_type_desc || '').toLowerCase();
      return String(selectedTemplate.templateName).toLowerCase().includes(usageDesc);
    });
    if (!matchedUsage) {
      const primaryUsage = usages.find(usage => usage.primary_usage_f);
      if (!primaryUsage) {
        const vals = usages.map(u => u.net_rentable_area);
        const nums = vals.filter(v => v != null) as number[];
        const biggest = nums.length ? Math.max(...nums) : undefined;
        if (biggest == null) {
          return null;
        }
        const usageWithBiggest = usages.find(u => u.net_rentable_area === biggest);
        return usageWithBiggest;
      }
      return primaryUsage;
    }
    return matchedUsage;
  }
  return null;
};

export const getPropertyTypeNRATotal = (record, options) => {
  const usage = getAvailabilityDetailsByUsageAndTemplate(record, options);
  if (!usage) {
    return defaultTxt;
  }
  return getUsageFieldByTemplate(usage, 'net_rentable_area', 'net_rentable_area_uom_desc');
};

export const getUsageSapceAvailable = (record, options) => {
  const usage = getAvailabilityDetailsByUsageAndTemplate(record, options);
  if (!usage) {
    return defaultTxt;
  }
  return getUsageFieldByTemplate(usage, 'total_available_surface_area', 'total_available_surface_area_uom_desc');
};

function priceAskingRate(
  record: ReportRecord,
  minMonthlyRate: DisplayFnArgumentsInfo,
  maxMonthlyRate: DisplayFnArgumentsInfo,
  minYearlyRate: DisplayFnArgumentsInfo,
  maxYearlyRate: DisplayFnArgumentsInfo,
  currencyLOV: LovValues[],
  propertyType: PropertyType
) {
  const isYearlyPref = UserSettings.isYearlyPreference();
  const askingRateVal = !!isYearlyPref
    ? getMinMaxRateInfo(record, propertyType, minYearlyRate, maxYearlyRate, currencyLOV)
    : getMinMaxRateInfo(record, propertyType, minMonthlyRate, maxMonthlyRate, currencyLOV);
  const checkAskingRateVal = askingRateVal != null && !isDash(askingRateVal);
  return checkAskingRateVal ? `${askingRateVal} (Lease)` : null;
}

function getMinMaxRateInfo(
  record: ReportRecord,
  propertyType: PropertyType,
  minRateField: DisplayFnArgumentsInfo,
  maxRateField: DisplayFnArgumentsInfo,
  currencyLOV: LovValues[]
) {
  const minRateValue = valueWithUomPrefix(record, propertyType, minRateField, currencyLOV);
  const maxRateValue = valueWithUomPrefix(record, propertyType, maxRateField, currencyLOV);
  let minMaxRate: string | string[] = '-';
  const checkMinRateVal = minRateValue != null && !isDash(minRateValue);
  const checkMaxRateVal = maxRateValue != null && !isDash(maxRateValue);
  if (checkMinRateVal && checkMaxRateVal) {
    minMaxRate = `${minRateValue} - ${maxRateValue}`;
  } else if (checkMinRateVal || checkMaxRateVal) {
    minMaxRate = checkMinRateVal ? minRateValue : maxRateValue;
  }
  return minMaxRate;
}

export function calcPercentage(dividend: string | number, divisor: string | number, digits): string {
  divisor = typeof divisor === 'number' ? divisor : parseInt(divisor);
  dividend = typeof dividend === 'number' ? dividend : parseInt(dividend);
  const val: number = divisor !== 0 ? (dividend / divisor) * 100 : 0;
  const result = formatNumber(val, 'en-US', digits);
  return result;
}

export function getFloorSuiteVals(record: ReportRecord, floor: DisplayFnArgumentsInfo, suite: DisplayFnArgumentsInfo) {
  const floorVal = mapAndFormatDynamicValue(record, floor);
  const suiteVal = mapAndFormatDynamicValue(record, suite);
  return `${floorVal} | ${suiteVal}`;
}

export function getListingagency(record) {
  // First get the Companies filtered by the role, digger can returns an object, an array or null
  let listingAgencies = digger(record, "company_contact_role_addresses[role_desc='Listing Representative']") || [];
  listingAgencies = Array.isArray(listingAgencies) ? listingAgencies : [listingAgencies];
  if (!listingAgencies.length) return '-';

  const getUniqueCompanies = new Set(listingAgencies.map(company => company.company_id));
  const formattedCompanies: string[] = [];
  // Format: Company | contact 1, contact 2, Company 2 | contact 1
  Array.from(getUniqueCompanies).forEach(compId => {
    const companyContacs = listingAgencies.filter(agen => agen.company_id === compId);
    const companyName = companyContacs[0].party_organization_display_name || '-';
    const contacts = companyContacs.map(comp => comp.familiar_name).filter(Boolean);
    formattedCompanies.push(`${companyName} | ${contacts.join(', ') || '-'}`);
  });
  return formattedCompanies.join(', ');
}

export function displayCompanyNameAndFamiliarNameByRoles(...roles: string[]) {
  return (record: ReportRecord, _, _arg) => {
    const contacts = getCompanyAndContactInfoByRoles(...roles);
    return Array.isArray(record.data) ? record.data.map(c => contacts(c)).join(', ') : contacts(record);
  };
}

// DISPLAY FUNCTIONS MAPPING
export function US_AVAILABILITY_COMMON_HEADER_FIELDS(propertyType: PropertyType): TemplateFieldsDisplayFn {
  const displayFnPropertyType = propertyTypeDisplayFnFactory(propertyType);
  function formatAskingRateValues(record, options, minMonthly, maxMonthly, minYearly, maxYearly, rateType) {
    const lov$ = currencyLov(options);
    return lov$.pipe(
      map((currencyLOV: LovValues[]) => {
        const rateTypeVal = displayFnPropertyType(record, options, rateType);
        const isYearlyPref = UserSettings.isYearlyPreference();
        const suffix = isYearlyPref ? 'Yr' : 'Mo';
        const askingRateVal = isYearlyPref
          ? getMinMaxRateInfo(record, propertyType, minYearly, maxYearly, currencyLOV)
          : getMinMaxRateInfo(record, propertyType, minMonthly, maxMonthly, currencyLOV);
        const result = `${askingRateVal} ${rateTypeVal}/${suffix}`;
        return result;
      })
    );
  }
  return {
    26: {
      // 26 - Property Name
      displayFn: (record: ReportRecord, _, arg) => {
        const values = concatValuesBy(defaultTxt, mapAndFormatDynamicValue(record, arg));
        return values;
      },
    },
    27: {
      // 27 - Property Address
      displayFn: (record: ReportRecord, options, ...args) => {
        const finalArgs = sortAttributesByFieldId(options.locale, args, 27);
        const values = finalArgs.map(arg => mapAndFormatDynamicValue(record, arg));
        return concatValuesBy(', ', ...values).replace(/,(?=[^,]*$)/, ''); // Removing last comma before PIN
      },
      routeFn: ({ data }, settings) => {
        const entityRecord = Array.isArray(data) ? data[0] : data;
        if (settings?.subAttribute) {
          return [
            '/records',
            settings.subAttribute.urlSection,
            digger(entityRecord, `${settings.subAttribute.dataPath}.id`),
            'details',
          ];
        }
        return ['/records', 'availability', digger(entityRecord, 'entity.id'), 'details'];
      },
    },
    50: {
      displayFn: (record: ReportRecord, options) => {
        const usage = getAvailabilityDetailsByUsageAndTemplate(record, options);
        if (!usage) {
          return '-';
        }
        const marketVal = usage.lig_geography.market_desc;
        const submarketVal = usage.lig_geography.submarket_desc;
        const districtVal = usage.lig_geography.district_desc;
        const neighborhoodVal = usage.lig_geography.neighborhood_desc;
        const locationValues = [marketVal, submarketVal, districtVal, neighborhoodVal].filter(
          val => !isDash(val) && val != null
        );
        return concatValuesBy(' - ', ...locationValues);
      },
    }, // 50 - Location
    178: {
      displayFn: (record: ReportRecord, _options, _args) => {
        return getPropertyTypeNRATotal(record as any, _options as any);
      },
    }, // 178 - Size
    182: {
      displayFn: (record: ReportRecord, _options, _nra, _avail, _vacant) => {
        return getAvailVacant(record as any, _options as any);
      },
    }, // 182 - Avail | Vacant

    // 184 - Space Available
    184: {
      displayFn: (record: ReportRecord, _options, _args) => {
        return getUsageSapceAvailable(record as any, _options as any);
      },
    },
    185: {
      displayFn: formatAskingRateValues,
    }, // 185 - Asking Rate (for Direct Lease)
    186: {
      displayFn: formatAskingRateValues,
    }, // 186 - Asking Rate (for SubLease)
    189: { displayFn: displayFnWrapper }, // 189 - Asking Rate Type for Direct Lease)
  };
}

export function US_AVAILABILITY_COMMON_BODY_FIELDS(propertyType: PropertyType): TemplateFieldsDisplayFn {
  const displayFnPropertyType = propertyTypeDisplayFnFactory(propertyType);
  function formatPrice(
    record: ReportRecord,
    options: DisplayFunctionOptions,
    askingSalePrice: DisplayFnArgumentsInfo,
    minMonthlyRate: DisplayFnArgumentsInfo,
    maxMonthlyRate: DisplayFnArgumentsInfo,
    minYearlyRate: DisplayFnArgumentsInfo,
    maxYearlyRate: DisplayFnArgumentsInfo
  ) {
    return currencyLov(options).pipe(
      map((currencyLOV: LovValues[]) => {
        const askingPriceVal = valueWithUomPrefix(record, propertyType, askingSalePrice, currencyLOV);
        const askingSalePriceVal = !!askingPriceVal && !isDash(askingPriceVal) ? `${askingPriceVal} (Sale)` : null;
        const askingRateVal = priceAskingRate(
          record,
          minMonthlyRate,
          maxMonthlyRate,
          minYearlyRate,
          maxYearlyRate,
          currencyLOV,
          propertyType
        );
        let price = '-';
        if (askingSalePriceVal != null && askingRateVal != null) {
          price = `${askingSalePriceVal} | ${askingRateVal}`;
        } else if (askingSalePriceVal != null || askingRateVal != null) {
          price = askingSalePriceVal != null ? askingSalePriceVal : askingRateVal;
        }
        return price;
      })
    );
  }
  return {
    193: { displayFn: displayFnWrapper }, // 193 - Available Space
    194: {
      displayFn: (record: ReportRecord, _, maxSpace, minSpace) => {
        const maxVal = mapAndFormatDynamicValue(record, maxSpace);
        const minVal = mapAndFormatDynamicValue(record, minSpace);
        const values = concatValuesBy(' - ', minVal, maxVal);
        return values;
      },
    }, // 194 - Min - Max Space
    195: {
      displayFn: (record: Record<string, any>) => {
        const usage = record?.data[0]?.property?.property_usage;
        if (!usage) {
          return '-';
        }
        const propertyTypeVal = getPropertyTypeLabel(usage.ref_property_usage_type_desc);
        const subtypeVal = usage.ref_property_usage_sub_type_desc;
        const classTypeVal = usage.ref_property_class_type_desc;
        const values = [propertyTypeVal, subtypeVal, classTypeVal].filter(v => v).join(' | ');
        return values ?? '-';
      },
    }, // 195 - Building Info
    196: { displayFn: formatPrice }, // 196 - Price
    199: { displayFn: displayFnPropertyType }, // 199 - Lease Type
    200: { displayFn: displayFnPropertyType }, // 200 - Available On
    201: {
      // 201 - Vacant (Y/N)
      displayFn: (record: ReportRecord, options, vacant) => {
        const vacantVal = mapAndFormatDynamicValue(record, vacant) as string;
        const result = formatYesOrNoValues(vacantVal, options);
        return result;
      },
    },
    188: {
      // 188 - Sub Lessor 5423812450539875
      displayFn: displayCompanyNameAndFamiliarNameByRoles('Sub Lessor'),
    },
    202: {
      // 202 - Listing Rep
      displayFn: displayCompanyNameAndFamiliarNameByRoles('Listing Representative'),
    },
    203: {
      // 203 - Lessor/Seller
      displayFn: displayCompanyNameAndFamiliarNameByRoles('Lessor', 'Seller'),
    },
  };
}

export const US_AVAILABILITY_COMMON_FOOTER_FIELDS = {
  204: { displayFn: displayFnWrapper }, // 204 - Notes
};
